import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Pill, CheckCircle, AlertTriangle, XCircle, Clock,
  Search, Filter, ChevronRight
} from 'lucide-react';
import { getFormulary, updateFormularyEntry } from '../services/api';

const statusColors = {
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  under_review: 'bg-amber-100 text-amber-700 border-amber-200',
  blocked: 'bg-red-100 text-red-700 border-red-200',
  replaced: 'bg-slate-100 text-slate-700 border-slate-200'
};

const substitutionLabels = {
  immediate_opportunity: { label: 'Immediate Opportunity', color: 'bg-emerald-500', icon: CheckCircle },
  needs_review: { label: 'Needs Review', color: 'bg-amber-500', icon: Clock },
  not_recommended: { label: 'Not Recommended', color: 'bg-red-500', icon: XCircle },
  no_alternative: { label: 'No Alternative', color: 'bg-slate-400', icon: Pill }
};

function FormularyRow({ drug }) {
  const substitution = substitutionLabels[drug.substitution_status] || substitutionLabels.no_alternative;
  const StatusIcon = substitution.icon;
  
  return (
    <tr className="data-table-row" data-testid={`formulary-row-${drug.id}`}>
      <td className="data-table-cell">
        <Link to={`/drug/${drug.id}`} className="flex items-center gap-3 group">
          <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
            <Pill className="w-4 h-4 text-slate-500" />
          </div>
          <div>
            <p className="font-medium text-slate-900 group-hover:text-[#0A2540] transition-colors">
              {drug.brand_name}
            </p>
            <p className="text-xs text-slate-500">{drug.generic_name}</p>
          </div>
        </Link>
      </td>
      <td className="data-table-cell">
        <span className="badge-neutral">{drug.therapeutic_area}</span>
      </td>
      <td className="data-table-cell text-right font-medium">
        ${((drug.reference_cost_usd || 0) / 12).toLocaleString()}
      </td>
      <td className="data-table-cell text-center">
        {drug.generic_count || 0}
      </td>
      <td className="data-table-cell">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${substitution.color}`} />
          <span className="text-sm">{substitution.label}</span>
        </div>
      </td>
      <td className="data-table-cell">
        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
          statusColors[drug.formulary_status] || statusColors.active
        }`}>
          {(drug.formulary_status || 'active').replace('_', ' ')}
        </span>
      </td>
      <td className="data-table-cell text-right">
        <Link 
          to={`/drug/${drug.id}`}
          className="text-[#0A2540] hover:underline text-sm"
        >
          View →
        </Link>
      </td>
    </tr>
  );
}

export default function FormularyPage() {
  const [drugs, setDrugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await getFormulary();
        setDrugs(res.data.formulary || []);
      } catch (error) {
        console.error('Failed to fetch formulary:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const filteredDrugs = drugs.filter(drug => {
    const matchesSearch = !searchQuery || 
      drug.brand_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      drug.generic_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || 
      drug.substitution_status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const counts = {
    immediate: drugs.filter(d => d.substitution_status === 'immediate_opportunity').length,
    needs_review: drugs.filter(d => d.substitution_status === 'needs_review').length,
    not_recommended: drugs.filter(d => d.substitution_status === 'not_recommended').length,
    no_alternative: drugs.filter(d => d.substitution_status === 'no_alternative').length
  };

  if (loading) {
    return (
      <div className="p-6" data-testid="formulary-loading">
        <div className="skeleton h-8 w-48 mb-6" />
        <div className="skeleton h-96 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="formulary-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Formulary View</h1>
          <p className="text-slate-500">Current drug inventory and substitution readiness</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <button 
          onClick={() => setFilterStatus('immediate_opportunity')}
          className={`card-premium p-4 text-left transition-colors ${
            filterStatus === 'immediate_opportunity' ? 'ring-2 ring-[#0A2540]' : ''
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-900">{counts.immediate}</p>
              <p className="text-xs text-slate-500">Immediate Opportunity</p>
            </div>
          </div>
        </button>
        <button 
          onClick={() => setFilterStatus('needs_review')}
          className={`card-premium p-4 text-left transition-colors ${
            filterStatus === 'needs_review' ? 'ring-2 ring-[#0A2540]' : ''
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-900">{counts.needs_review}</p>
              <p className="text-xs text-slate-500">Needs Review</p>
            </div>
          </div>
        </button>
        <button 
          onClick={() => setFilterStatus('not_recommended')}
          className={`card-premium p-4 text-left transition-colors ${
            filterStatus === 'not_recommended' ? 'ring-2 ring-[#0A2540]' : ''
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-900">{counts.not_recommended}</p>
              <p className="text-xs text-slate-500">Not Recommended</p>
            </div>
          </div>
        </button>
        <button 
          onClick={() => setFilterStatus('no_alternative')}
          className={`card-premium p-4 text-left transition-colors ${
            filterStatus === 'no_alternative' ? 'ring-2 ring-[#0A2540]' : ''
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <Pill className="w-5 h-5 text-slate-500" />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-900">{counts.no_alternative}</p>
              <p className="text-xs text-slate-500">No Alternative</p>
            </div>
          </div>
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by drug name..."
            className="input-field pl-10"
          />
        </div>
        {filterStatus !== 'all' && (
          <button
            onClick={() => setFilterStatus('all')}
            className="btn-ghost text-sm"
          >
            Clear filter
          </button>
        )}
      </div>

      {/* Formulary Table */}
      <div className="card-premium overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="data-table-header text-left">Drug</th>
              <th className="data-table-header text-left">Therapeutic Area</th>
              <th className="data-table-header text-right">Monthly Cost</th>
              <th className="data-table-header text-center">Generics</th>
              <th className="data-table-header text-left">Substitution Status</th>
              <th className="data-table-header text-left">Formulary Status</th>
              <th className="data-table-header text-right"></th>
            </tr>
          </thead>
          <tbody>
            {filteredDrugs.map(drug => (
              <FormularyRow key={drug.id} drug={drug} />
            ))}
          </tbody>
        </table>
        
        {filteredDrugs.length === 0 && (
          <div className="py-12 text-center">
            <Pill className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No drugs found</p>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
        <p className="text-sm text-slate-600">
          <strong>{filteredDrugs.length}</strong> drugs displayed • 
          <strong className="text-emerald-600 ml-2">{counts.immediate}</strong> with immediate opportunities • 
          <strong className="text-amber-600 ml-2">{counts.needs_review}</strong> needing review
        </p>
      </div>
    </div>
  );
}
